import { useToaster } from '@innovationdepartment/proxima-ui';
import { useShowSpinner } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { payloadParseError } from './BrandWorkflows.ManualTriggersList';
import type { BrandWorkflowRow, BrandWorkflowTab, KickOffError } from './BrandWorkflows.types';
import { BRAND_WORKFLOW_TABS } from './BrandWorkflows.types';
import BrandWorkflowsView from './BrandWorkflows.View';
import useBrandWorkflowsApi from './useBrandWorkflowsApi';
import { pickSchedulerByType } from './workflowFormatters';

const BrandWorkflows = () => {
  const { triggerExecution } = useBrandWorkflowsApi();
  const api = useBrandWorkflowsApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { showToaster } = useToaster();

  const [rows, setRows] = useState<BrandWorkflowRow[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [retryingExecutionId, setRetryingExecutionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<BrandWorkflowTab>(BRAND_WORKFLOW_TABS.Latest);
  const [expandedWorkflowType, setExpandedWorkflowType] = useState<string | null>(null);
  const [kickingOffWorkflowType, setKickingOffWorkflowType] = useState<string | null>(null);
  const [kickOffError, setKickOffError] = useState<KickOffError | null>(null);
  const [loading, setLoading] = useState(true);

  useShowSpinner({ show: loading });

  const loadWorkflows = useCallback(async () => {
    try {
      const [executionsResult, schedulersResult] = await Promise.all([
        apiRef.current.getLatestExecutions(),
        apiRef.current.getSchedulers()
      ]);

      if (executionsResult.data) {
        const schedulerByType = pickSchedulerByType(schedulersResult.data ?? []);
        setRows(
          executionsResult.data.map(execution => ({
            ...execution,
            scheduler: schedulerByType.get(execution.workflowType) ?? null
          }))
        );
        setLastUpdatedAt(new Date());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const onRetryFromFailed = async (executionId: string) => {
    setRetryingExecutionId(executionId);
    try {
      const result = await api.retryFromFailed(executionId);

      if (result.success) {
        showToaster({ message: 'Retry started', variant: 'success' });
        // Flip the row immediately; the refetch below confirms against the server
        setRows(previous =>
          previous.map(row =>
            row.executionId === executionId
              ? { ...row, status: 'InProgress' as const, isFirstRun: false }
              : row
          )
        );
      }

      await loadWorkflows();
    } finally {
      setRetryingExecutionId(null);
    }
  };

  const onToggleManualTrigger = (workflowType: string) => {
    setKickOffError(null);
    setExpandedWorkflowType(previous => (previous === workflowType ? null : workflowType));
  };

  const onKickOff = async (workflowType: string, payloadText: string) => {
    const parseError = payloadParseError(payloadText);
    if (parseError) {
      setKickOffError({ workflowType, message: parseError });
      return;
    }

    setKickingOffWorkflowType(workflowType);
    setKickOffError(null);
    try {
      const result = await triggerExecution(workflowType, JSON.parse(payloadText));

      if (result.error) {
        setKickOffError({ workflowType, message: result.error });
        return;
      }

      showToaster({ message: 'Workflow triggered', variant: 'success' });
      setExpandedWorkflowType(null);
      await loadWorkflows();
    } finally {
      setKickingOffWorkflowType(null);
    }
  };

  return (
    <BrandWorkflowsView
      rows={rows}
      lastUpdatedAt={lastUpdatedAt}
      activeTab={activeTab}
      retryingExecutionId={retryingExecutionId}
      expandedWorkflowType={expandedWorkflowType}
      kickingOffWorkflowType={kickingOffWorkflowType}
      kickOffError={kickOffError}
      onTabChange={setActiveTab}
      onRetryFromFailed={onRetryFromFailed}
      onToggleManualTrigger={onToggleManualTrigger}
      onKickOff={onKickOff}
    />
  );
};

export default BrandWorkflows;
